import { FormGroup } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { CodeUoM, StageItemDetail } from '@jms/model';
import { PricingService } from '@jms/ui/ui.standalone.services/pricing.service';
import { buildBuyPriceRequest, buildSellPriceRequest } from '../../models/job.item.pricing';
import { payableForcesZeroBuy } from '@workbuddy/surfaces/embedded-item';
import type { ItemEditorField, ItemUpdateField } from '../../models/job.item.model';

/**
 * Controls that do not have their own readonly rule — they follow another field's gate
 * (custom-price flags follow the price they hold; pending itemStatus follows status).
 */
export const ITEM_EDITOR_RULE_FOR_CONTROL: Partial<Record<ItemUpdateField, ItemEditorField>> = {
  customBuyPrice: 'buyPrice',
  customSellPrice: 'sellPrice',
  itemStatus: 'status',
};

/**
 * Owning stage's `updatedAt` in the shape UpdateItem / DeleteItem expect. Both item editors send it —
 * `assertStageItemConcurrency` rejects the write outright when it is missing.
 */
export function normalizeItemStageUpdatedAt(value: unknown): string | undefined {

  if (value == null) return undefined;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

/** Catalogue UoMs plus the line's own when a reconfigure dropped it from the list. */
export function resolveItemUoms(detail: StageItemDetail | null | undefined): CodeUoM[] {

  if (!detail) return [];

  const uoms = detail.uoms ?? [];

  return detail.codeUoM && !uoms.some(uom => uom._id === detail.codeUoM._id)
    ? [detail.codeUoM, ...uoms]
    : uoms;
}

/** Buy-side twin of the shared `applyInvoicingPriceRule` — Not Payable zeroes what a bill was raised from. */
export function applyPayablePriceRule(form: FormGroup, payable: boolean): boolean {

  if (!payableForcesZeroBuy(payable)) return false;

  form.patchValue({ buyPrice: 0, customBuyPrice: false });
  return true;
}

/**
 * Read the pricebook for one side of the line. Returns null when the field is locked, custom, or
 * the book has no answer — leaves the figure alone in those cases.
 */
export async function repriceItemField(options: {
  field: 'buyPrice' | 'sellPrice';
  detail: StageItemDetail;
  codeUoM: CodeUoM;
  isCustom: boolean;
  isReadonly: boolean;
  pricingService: PricingService;
}): Promise<number | null> {

  const { field, detail, codeUoM, isCustom, isReadonly, pricingService } = options;

  if (!detail || isReadonly || isCustom || !codeUoM) return null;

  const request = field === 'buyPrice'
    ? buildBuyPriceRequest(detail.name, codeUoM, detail.stage)
    : buildSellPriceRequest(detail.name, codeUoM, detail.stage);

  const price = (await lastValueFrom(pricingService.getCodePrice(request)))?.[0];
  const next = field === 'buyPrice' ? price?.buyPrice : price?.sellPrice;

  return next ?? null;
}
